import React, { useState } from 'react';
import styled from 'styled-components';
import { SharedLayoutUltimate } from '../components/layout/SharedLayoutUltimate';
import {
  PageWrapper,
  HeroSection,
  Container,
  PageTitle,
  PageSubtitle,
  PremiumCard,
  PrimaryButton,
  SecondaryButton,
  FormGroup,
  Label,
  Input,
  Select,
  Section,
  InfoBox
} from '../styles/PremiumStyles';
import { theme } from '../styles/theme';

// Enhanced form elements for senior-friendly design
const EnhancedInput = styled(Input)`
  font-size: 16px;
  padding: 16px;
  min-height: 52px;
  border: 2px solid #e0e0e0;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 16px;
    padding: 15px;
    min-height: 50px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 16px; /* Prevents zoom on iOS */
    padding: 14px;
    min-height: 48px;
  }
  
  &:focus {
    border-color: #FFD700;
    box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.2);
  }
`;

const EnhancedSelect = styled(Select)`
  font-size: 16px;
  padding: 16px;
  min-height: 52px;
  border: 2px solid #e0e0e0;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 16px;
    padding: 15px;
    min-height: 50px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 16px;
    padding: 14px;
    min-height: 48px;
  }
  
  &:focus {
    border-color: #FFD700;
    box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.2);
  }
`;

const EnhancedLabel = styled(Label)`
  font-size: 15px;
  font-weight: 600;
  color: #1a2332;
  margin-bottom: 8px;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 14px;
    margin-bottom: 6px;
  }
`;

const AnalysisCard = styled(PremiumCard)`
  max-width: 800px;
  margin: -50px auto 50px;
  background: white;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    margin: -30px 15px 30px;
  }
`;

const FormCard = styled.div`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.95) 100%);
  border: 2px solid rgba(255, 215, 0, 0.3);
  border-radius: 20px;
  padding: 30px;
  margin-bottom: 20px;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: 25px;
    margin-bottom: 18px;
    border-radius: 15px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 20px;
    margin-bottom: 15px;
    border-radius: 12px;
  }
  
  h3 {
    font-size: 24px;
    color: #1a2332;
    margin-bottom: 25px;
    font-weight: 700;
    text-align: center;
    line-height: 1.3;
    
    @media (max-width: ${theme.breakpoints.tablet}) {
      font-size: 22px;
      margin-bottom: 22px;
    }
    
    @media (max-width: ${theme.breakpoints.mobile}) {
      font-size: 20px;
      margin-bottom: 20px;
      line-height: 1.4;
    }
  }
`;

const EnhancedTextArea = styled.textarea`
  width: 100%;
  padding: 16px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 16px;
  min-height: 140px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s;
  line-height: 1.5;
  
  &:focus {
    outline: none;
    border-color: #FFD700;
    box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.2);
  }
  
  &::placeholder {
    color: #777;
    line-height: 1.5;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: 15px;
    min-height: 130px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 14px;
    font-size: 16px; /* Prevents zoom on iOS */
    min-height: 120px;
  }
`;

const OptionalSection = styled.div`
  margin-top: 30px;
  padding-top: 30px;
  border-top: 2px solid rgba(255, 215, 0, 0.2);
  
  .section-header {
    text-align: center;
    margin-bottom: 25px;
    
    h4 {
      font-size: 20px;
      color: #1a2332;
      margin-bottom: 12px;
      font-weight: 700;
      line-height: 1.3;
    }
    
    p {
      color: #666;
      font-size: 15px;
      line-height: 1.6;
      max-width: 600px;
      margin: 0 auto;
    }
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    margin-top: 25px;
    padding-top: 25px;
    
    .section-header {
      h4 {
        font-size: 19px;
      }
      
      p {
        font-size: 14px;
        line-height: 1.5;
      }
    }
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    margin-top: 20px;
    padding-top: 20px;
    
    .section-header {
      h4 {
        font-size: 18px;
        line-height: 1.4;
      }
      
      p {
        font-size: 14px;
        line-height: 1.5;
        text-align: left;
      }
    }
  }
`;

const SuccessMessage = styled.div`
  background: #e8f5e9;
  border: 2px solid #4caf50;
  border-radius: 12px;
  padding: 40px;
  text-align: center;
  margin-top: 20px;
  
  h3 {
    color: #2e7d32;
    font-size: 26px;
    margin-bottom: 18px;
    font-weight: 700;
    line-height: 1.3;
  }
  
  p {
    color: #2e7d32;
    font-size: 17px;
    line-height: 1.6;
    max-width: 500px;
    margin: 0 auto;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: 30px;
    
    h3 {
      font-size: 24px;
      margin-bottom: 16px;
    }
    
    p {
      font-size: 16px;
    }
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 25px 20px;
    
    h3 {
      font-size: 22px;
      margin-bottom: 15px;
      line-height: 1.4;
    }
    
    p {
      font-size: 15px;
      line-height: 1.5;
    }
  }
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 18px;
  }
  
  @media (max-width: 650px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;


const EnhancedPrimaryButton = styled(PrimaryButton)`
  font-size: 18px;
  padding: 18px 32px;
  min-height: 56px;

  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 17px;
    padding: 16px 28px;
    min-height: 54px;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 16px;
    padding: 16px 24px;
    min-height: 52px;
    width: 100%;
  }
`;

const ConsentContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 30px;
  padding: 20px;
  background: rgba(255, 215, 0, 0.05);
  border-radius: 8px;

  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: 10px;
    padding: 15px;
    margin-top: 25px;
  }
`;

const ConsentCheckbox = styled.input`
  width: 20px;
  height: 20px;
  min-width: 20px;
  margin-top: 2px;
  cursor: pointer;

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 18px;
    height: 18px;
    min-width: 18px;
  }
`;

const ConsentLabel = styled.label`
  font-size: 15px;
  color: #1a2332;
  line-height: 1.6;
  cursor: pointer;

  a {
    color: #0066cc;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
      color: #004499;
    }
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 14px;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  overflow-y: auto;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 10px;
    align-items: flex-start;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);

  @media (max-width: ${theme.breakpoints.mobile}) {
    max-height: 95vh;
    border-radius: 8px;
    margin: 10px 0;
  }
`;

const ModalHeader = styled.div`
  position: sticky;
  top: 0;
  background: white;
  padding: 25px 30px;
  border-bottom: 2px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 12px 12px 0 0;
  z-index: 1;

  h3 {
    font-size: 24px;
    color: #1a2332;
    margin: 0;
    font-weight: 700;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 20px 15px;

    h3 {
      font-size: 20px;
    }
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 28px;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.1);
    color: #333;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 24px;
    width: 28px;
    height: 28px;
  }
`;

const ModalBody = styled.div`
  padding: 30px;

  h4 {
    font-size: 20px;
    color: #1a2332;
    margin: 25px 0 12px 0;
    font-weight: 700;

    &:first-child {
      margin-top: 0;
    }
  }

  p {
    font-size: 15px;
    line-height: 1.7;
    color: #333;
    margin-bottom: 15px;
  }

  ul {
    margin: 15px 0;
    padding-left: 20px;

    li {
      font-size: 15px;
      line-height: 1.7;
      color: #333;
      margin-bottom: 10px;
    }
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 20px 15px;

    h4 {
      font-size: 18px;
      margin: 20px 0 10px 0;
    }

    p, ul li {
      font-size: 14px;
      line-height: 1.6;
    }
  }
`;

interface FormData {
  // Required fields
  name: string;
  email: string;
  phone: string;
  concerns: string;
  
  // Optional fields
  currentCarrier: string;
  currentProduct: string;
  currentAllocationRates: string;
  premiumType: string;
  purchaseDate: string;
  totalDeposited: string;
  currentAccumulatedValue: string;
  currentSurrenderValue: string;
  penaltyFreeWithdrawalPercent: string;
  penaltyFreeWithdrawalDollar: string;
  systematicWithdrawals: string;
  withdrawalAmount: string;
  actualGrowthPercent: string;
}

export const DeepDive: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    // Required fields
    name: '',
    email: '',
    phone: '',
    concerns: '',

    // Optional fields
    currentCarrier: '',
    currentProduct: '',
    currentAllocationRates: '',
    premiumType: '',
    purchaseDate: '',
    totalDeposited: '',
    currentAccumulatedValue: '',
    currentSurrenderValue: '',
    penaltyFreeWithdrawalPercent: '',
    penaltyFreeWithdrawalDollar: '',
    systematicWithdrawals: '',
    withdrawalAmount: '',
    actualGrowthPercent: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);

  // Anti-spam fields
  const [honeypot, setHoneypot] = useState('');
  const [formLoadTime] = useState(Date.now());

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setSubmitSuccess(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      concerns: '',
      currentCarrier: '',
      currentProduct: '',
      currentAllocationRates: '',
      premiumType: '',
      purchaseDate: '',
      totalDeposited: '',
      currentAccumulatedValue: '',
      currentSurrenderValue: '',
      penaltyFreeWithdrawalPercent: '',
      penaltyFreeWithdrawalDollar: '',
      systematicWithdrawals: '',
      withdrawalAmount: '',
      actualGrowthPercent: ''
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.name || !formData.email || !formData.phone) {
      alert('Please fill in all required fields.');
      return;
    }

    // Anti-spam validation
    if (honeypot) {
      console.warn('Spam detected: honeypot field filled');
      return;
    }

    const timeSpent = Date.now() - formLoadTime;
    if (timeSpent < 3000) {
      console.warn('Spam detected: form submitted too quickly');
      return;
    }

    setIsSubmitting(true);

    try {
      const emailData = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        insurance_company: formData.currentCarrier || 'Not provided',
        annuity_product: formData.currentProduct || 'Not provided',
        submission_type: 'deep_dive_analysis',
        formLoadTime: formLoadTime,
        website: honeypot,
        message: `
DEEP DIVE ANALYSIS REQUEST
===========================

Required Information:
Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}
Concerns: ${formData.concerns}

Optional Information:
Current Carrier: ${formData.currentCarrier || 'Not provided'}
Current Product: ${formData.currentProduct || 'Not provided'}
Current Allocation/Rates: ${formData.currentAllocationRates || 'Not provided'}
Single Premium or Flexible: ${formData.premiumType || 'Not provided'}
Purchase Date: ${formData.purchaseDate || 'Not provided'}
Total Deposited: ${formData.totalDeposited || 'Not provided'}
Current Accumulated Value: ${formData.currentAccumulatedValue || 'Not provided'}
Current Surrender Value: ${formData.currentSurrenderValue || 'Not provided'}
Penalty Free Withdrawal (%): ${formData.penaltyFreeWithdrawalPercent || 'Not provided'}
Penalty Free Withdrawal ($): ${formData.penaltyFreeWithdrawalDollar || 'Not provided'}
Systematic Withdrawals/RMDs: ${formData.systematicWithdrawals || 'Not provided'}
Withdrawal Amount: ${formData.withdrawalAmount || 'Not provided'}
Approx Growth Per Year (%): ${formData.actualGrowthPercent || 'Not provided'}

Submitted: ${new Date().toLocaleString('en-US', { timeZone: 'America/New_York', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric', hour12: true })}
        `.trim()
      };

      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data: emailData }),
      });

      if (response.ok) {
        setSubmitSuccess(true);
        // Don't auto-reset - let user control when to reset
      } else {
        alert('There was an error submitting your request. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.name && formData.email && formData.phone && consentChecked;

  return (
    <SharedLayoutUltimate>
      <PageWrapper>
        <HeroSection style={{ color: 'white' }}>
          <Container>
            <PageTitle style={{ color: 'white' }}>
              <span className="gold">Deep Dive</span> Annuity Analysis
            </PageTitle>
            <PageSubtitle style={{ color: 'rgba(255, 255, 255, 0.95)' }}>
              Compare up to 10 annuities with real market data
            </PageSubtitle>
          </Container>
        </HeroSection>
        
        <AnalysisCard>
          {!submitSuccess ? (
            <form onSubmit={handleSubmit}>
              <h2 style={{ 
                fontSize: '36px', 
                marginBottom: '30px', 
                color: '#1a2332',
                textAlign: 'center',
                fontWeight: '800'
              }}>
                Request Your <span className="gold">Deep Dive</span> Analysis
              </h2>
              
              <InfoBox $variant="info">
                <h4>📊 Perfect for agents or anyone who needs to analyze multiple products</h4>
                <p>Get comprehensive analysis with real data, not hypothetical illustrations.</p>
              </InfoBox>
              
              <FormCard>
                <h3>Required Information</h3>
                
                <GridContainer>
                  <FormGroup>
                    <EnhancedLabel htmlFor="name">Name *</EnhancedLabel>
                    <EnhancedInput
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      required
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <EnhancedLabel htmlFor="email">Email *</EnhancedLabel>
                    <EnhancedInput
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      required
                    />
                  </FormGroup>
                </GridContainer>
                
                <FormGroup>
                  <EnhancedLabel htmlFor="phone">Phone *</EnhancedLabel>
                  <EnhancedInput
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Your phone number"
                    required
                  />
                </FormGroup>
                
                <FormGroup>
                  <EnhancedLabel htmlFor="concerns">What are your concerns about this contract?</EnhancedLabel>
                  <EnhancedTextArea
                    id="concerns"
                    name="concerns"
                    value={formData.concerns}
                    onChange={handleChange}
                    placeholder="Please describe your concerns, questions, or what you'd like to know about this annuity contract..."
                  />
                </FormGroup>
              </FormCard>
              
              <OptionalSection>
                <div className="section-header">
                  <h4>Optional Information</h4>
                  <p>The following fields are optional but encouraged. Provide as much information as you can about the contract you are considering, currently own, or recommending.</p>
                </div>
                
                <GridContainer>
                  <FormGroup>
                    <EnhancedLabel htmlFor="currentCarrier">Current Carrier</EnhancedLabel>
                    <EnhancedInput
                      type="text"
                      id="currentCarrier"
                      name="currentCarrier"
                      value={formData.currentCarrier}
                      onChange={handleChange}
                      placeholder="e.g., Allianz, American Equity"
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <EnhancedLabel htmlFor="currentProduct">Current Product</EnhancedLabel>
                    <EnhancedInput
                      type="text"
                      id="currentProduct"
                      name="currentProduct"
                      value={formData.currentProduct}
                      onChange={handleChange}
                      placeholder="e.g., Core Income 7"
                    />
                  </FormGroup>
                </GridContainer>
                
                <FormGroup>
                  <EnhancedLabel htmlFor="currentAllocationRates">Current Allocation/Rates</EnhancedLabel>
                  <EnhancedInput
                    type="text"
                    id="currentAllocationRates"
                    name="currentAllocationRates"
                    value={formData.currentAllocationRates}
                    onChange={handleChange}
                    placeholder="e.g., 10% S&P cap, 95% participation rate, 2.5% fixed"
                  />
                </FormGroup>
                
                <GridContainer>
                  <FormGroup>
                    <EnhancedLabel htmlFor="premiumType">Single Premium or Flexible</EnhancedLabel>
                    <EnhancedSelect
                      id="premiumType"
                      name="premiumType"
                      value={formData.premiumType}
                      onChange={handleChange}
                    >
                      <option value="">Select type...</option>
                      <option value="single">Single Premium</option>
                      <option value="flexible">Flexible Premium</option>
                    </EnhancedSelect>
                  </FormGroup>
                  
                  <FormGroup>
                    <EnhancedLabel htmlFor="purchaseDate">Purchase Date</EnhancedLabel>
                    <EnhancedInput
                      type="date"
                      id="purchaseDate"
                      name="purchaseDate"
                      value={formData.purchaseDate}
                      onChange={handleChange}
                    />
                  </FormGroup>
                </GridContainer>
                
                <GridContainer>
                  <FormGroup>
                    <EnhancedLabel htmlFor="totalDeposited">Total Deposited into Annuity</EnhancedLabel>
                    <EnhancedInput
                      type="text"
                      id="totalDeposited"
                      name="totalDeposited"
                      value={formData.totalDeposited}
                      onChange={handleChange}
                      placeholder="$100,000"
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <EnhancedLabel htmlFor="currentAccumulatedValue">Current Accumulated Value</EnhancedLabel>
                    <EnhancedInput
                      type="text"
                      id="currentAccumulatedValue"
                      name="currentAccumulatedValue"
                      value={formData.currentAccumulatedValue}
                      onChange={handleChange}
                      placeholder="$125,000"
                    />
                  </FormGroup>
                </GridContainer>
                
                <GridContainer>
                  <FormGroup>
                    <EnhancedLabel htmlFor="currentSurrenderValue">Current Surrender Value</EnhancedLabel>
                    <EnhancedInput
                      type="text"
                      id="currentSurrenderValue"
                      name="currentSurrenderValue"
                      value={formData.currentSurrenderValue}
                      onChange={handleChange}
                      placeholder="$120,000"
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <EnhancedLabel htmlFor="penaltyFreeWithdrawalPercent">Available Penalty Free Withdrawal (%)</EnhancedLabel>
                    <EnhancedInput
                      type="text"
                      id="penaltyFreeWithdrawalPercent"
                      name="penaltyFreeWithdrawalPercent"
                      value={formData.penaltyFreeWithdrawalPercent}
                      onChange={handleChange}
                      placeholder="10%"
                    />
                  </FormGroup>
                </GridContainer>
                
                <GridContainer>
                  <FormGroup>
                    <EnhancedLabel htmlFor="penaltyFreeWithdrawalDollar">Available Penalty Free Withdrawal ($)</EnhancedLabel>
                    <EnhancedInput
                      type="text"
                      id="penaltyFreeWithdrawalDollar"
                      name="penaltyFreeWithdrawalDollar"
                      value={formData.penaltyFreeWithdrawalDollar}
                      onChange={handleChange}
                      placeholder="$12,000"
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <EnhancedLabel htmlFor="systematicWithdrawals">Systematic Withdrawals/RMDs</EnhancedLabel>
                    <EnhancedSelect
                      id="systematicWithdrawals"
                      name="systematicWithdrawals"
                      value={formData.systematicWithdrawals}
                      onChange={handleChange}
                    >
                      <option value="">Select frequency...</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="annually">Annually</option>
                      <option value="none">None</option>
                    </EnhancedSelect>
                  </FormGroup>
                </GridContainer>
                
                <GridContainer>
                  <FormGroup>
                    <EnhancedLabel htmlFor="withdrawalAmount">Withdrawal Amount</EnhancedLabel>
                    <EnhancedInput
                      type="text"
                      id="withdrawalAmount"
                      name="withdrawalAmount"
                      value={formData.withdrawalAmount}
                      onChange={handleChange}
                      placeholder="$1,000/month"
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <EnhancedLabel htmlFor="actualGrowthPercent">Approx Actual Growth Per Year Since Inception (%)</EnhancedLabel>
                    <EnhancedInput
                      type="text"
                      id="actualGrowthPercent"
                      name="actualGrowthPercent"
                      value={formData.actualGrowthPercent}
                      onChange={handleChange}
                      placeholder="5.5%"
                    />
                  </FormGroup>
                </GridContainer>
              </OptionalSection>

              {/* Honeypot field - hidden from users, catches bots */}
              <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}>
                <label htmlFor="website">Website</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              <ConsentContainer>
                <ConsentCheckbox
                  type="checkbox"
                  id="consent"
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                />
                <ConsentLabel htmlFor="consent">
                  I agree to the{' '}
                  <a
                    onClick={(e) => {
                      e.preventDefault();
                      setShowDisclaimerModal(true);
                    }}
                  >
                    terms and conditions
                  </a>
                </ConsentLabel>
              </ConsentContainer>

              <div style={{ textAlign: 'center', marginTop: '40px' }}>
                <EnhancedPrimaryButton
                  type="submit"
                  disabled={isSubmitting || !isFormValid}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Deep Dive Request'}
                </EnhancedPrimaryButton>
              </div>
            </form>
          ) : (
            <SuccessMessage>
              <h3>✅ Deep Dive Request Submitted!</h3>
              <p>
                Thank you for your request! Our team will analyze your information and contact you within 1-2 business days with a comprehensive report tailored to your specific needs.
              </p>
              <div style={{ marginTop: '25px' }}>
                <SecondaryButton onClick={resetForm}>
                  Run Another Deep Dive
                </SecondaryButton>
              </div>
            </SuccessMessage>
          )}
        </AnalysisCard>
        
        <Section>
          <Container>
            <InfoBox $variant="success">
              <h4>🎯 What Makes Our Deep Dive Different?</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: '20px 0 0 0' }}>
                <li style={{ padding: '8px 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ color: '#10a37f', fontWeight: 'bold' }}>✓</span>
                  <span>Real daily market data since index inception - no backfilled hypotheticals</span>
                </li>
                <li style={{ padding: '8px 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ color: '#10a37f', fontWeight: 'bold' }}>✓</span>
                  <span>Permutation testing across all possible purchase dates</span>
                </li>
                <li style={{ padding: '8px 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ color: '#10a37f', fontWeight: 'bold' }}>✓</span>
                  <span>Actual cap and participation rate changes over time</span>
                </li>
                <li style={{ padding: '8px 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ color: '#10a37f', fontWeight: 'bold' }}>✓</span>
                  <span>Comprehensive analysis of multiple products</span>
                </li>
                <li style={{ padding: '8px 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ color: '#10a37f', fontWeight: 'bold' }}>✓</span>
                  <span>40 years of expertise built into every analysis</span>
                </li>
              </ul>
            </InfoBox>
            
            <div style={{ textAlign: 'center', marginTop: '60px' }}>
              <h3 style={{ fontSize: '32px', marginBottom: '20px', color: '#1a2332' }}>
                Need Help Getting Started?
              </h3>
              <p style={{ fontSize: '18px', marginBottom: '30px', color: '#666' }}>
                Our experts can walk you through the analysis process
              </p>
              <SecondaryButton onClick={() => window.location.href = 'tel:800-922-1894'}>
                📞 Call 800-922-1894
              </SecondaryButton>
            </div>
          </Container>
        </Section>
      </PageWrapper>

      {/* Disclaimer Modal */}
      {showDisclaimerModal && (
        <ModalOverlay onClick={() => setShowDisclaimerModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>Disclaimer</h3>
              <CloseButton onClick={() => setShowDisclaimerModal(false)}>
                ×
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              <h4>Purpose and Scope</h4>
              <p>
                We are sharing what we believe is the best methodology for establishing realistic expectations about the various allocation calculation methods and indices that are available in fixed index annuities today. These tests are designed to provide insight into the results a given calculation method would have produced in thousands of aggregated historical purchase scenarios, using actual historical index data wherever possible.
              </p>
              <p>
                This is not a comprehensive product test.
              </p>
              <p>
                Our analysis does not attempt to include full product details such as the impact of surrender charges, market value adjustments, bonus recaptures, or other contract-specific features. The focus is strictly on the mathematical process used to calculate interest for a given allocation, applied to actual historical index data.
              </p>

              <h4>Data Sources and Limitations</h4>
              <ul>
                <li><strong>Historical Data:</strong> Our primary commitment is to use only real, historical index data when setting expectations for products.</li>
                <li><strong>Hypothetical Data:</strong> We do not utilize hypothetical data for expectation-setting. In cases where indices do not have actual historical track records because of their recent development, we determine the actual annualized return since the index's inception and project it as a fixed rate going forward. We believe this provides a more realistic expectation than using hypothetical figures.</li>
                <li><strong>Hypothetical Comparisons:</strong> The only time we reference hypothetical information created by an index provider is to compare the hypothetical average return during the hypothetical period with the actual rate of return since inception. Typically, we find that the actual rate of return is significantly lower.</li>
              </ul>

              <h4>Testing Methodology</h4>
              <ul>
                <li><strong>Breadth of Testing:</strong> Our tests provide a broader display of information than a one-day-per-year test, which can obscure negative date lottery attributes of contracts. This approach allows a consumer to see a much larger spectrum of results.</li>
                <li><strong>Rate Drift:</strong> A significant risk for fixed index annuity holders is "rate drift," which refers to an insurance company's tendency to drop rates significantly after the first contract year or gradually reduce contract rates over time. Whether an insurance company has this tendency is a critical factor that in many cases can only be identified through experience and ownership, since many insurance companies do not disclose this information publicly. In our experience we have found that insurance companies that do not provide this information typically have a weaker renewal rate history and rate drift issues.</li>
                <li><strong>Selection of Allocation Methods:</strong> We do not test every allocation within a contract, only those we would consider using in the products we purchase for our own portfolio.</li>
                <li><strong>Carrier History:</strong> We maintain our own historical databases, which allows us to determine the rate drift tendency of many insurance companies. If we identify that an insurance company has a high tendency to rate drift, we include that tendency in our simulated analysis.</li>
              </ul>

              <h4>Product-Specific Information</h4>
              <p>
                If you inquire about a specific product or service, you will not find its name on the results provided by the deep dive test. Instead, we analyze the product you are inquiring about, determine which allocation method we believe has the highest return potential, and replicate that calculation method using actual historical data.
              </p>

              <h4>No Guarantee of Future Performance</h4>
              <p>
                Our deep dive test, which includes historical back-testing, does not guarantee future performance or results. However, we believe that this approach provides the most accurate expectations, given the available data, and helps you understand how date lottery, rate drift, and the differences found in the various calculation methods can affect the product.
              </p>

              <h4>Experience and Use</h4>
              <p>
                Our deep dive test and historical back-testing are not presented as the definitive answer to all questions about annuities. The only claim we make is that this tool is used by the Averill family and its agents, who together have more than 200 years of collective experience with annuity products, to choose annuities for their own portfolio.
              </p>

              <h4>Acknowledgement and Consent</h4>
              <p>
                By requesting one of these tests, you acknowledge the foregoing and agree and confirm that you understand:
              </p>
              <ul>
                <li>The information and test are offered to you at no cost or obligation.</li>
                <li>The information provided is for informational purposes only and should not be considered as financial, legal, or tax advice. You are strongly encouraged to consult with your accountant, attorney, tax professional, and licensed insurance agent before making any financial decisions or purchasing any insurance products.</li>
                <li>Everyone's situation is unique, and professional advice is essential to ensure that any product or strategy is appropriate for your specific needs and objectives.</li>
                <li>Fixed indexed annuities are insurance products and are not investments in the stock market or any security.</li>
                <li>Guarantees are backed by the financial strength and claims-paying ability of the issuing insurance company.</li>
                <li>Some of the information provided may have been developed or generated by artificial intelligence. Individuals are strongly encouraged to conduct their own independent analysis and verify the accuracy and applicability of any information provided.</li>
              </ul>
              <p>
                By proceeding you confirm that you understand and accept this disclaimer.
              </p>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </SharedLayoutUltimate>
  );
};